import type { Locale } from "../../../stores/locale";

// eisAgentPrompt returns the copyable prompt a user pastes into an AI coding
// agent (Claude Code etc.) running INSIDE their repo, so the agent inspects the
// repository and generates a tailored eis.yaml. The text is verbatim per locale
// — it is product copy, not a template, so do not paraphrase or interpolate.
const JA = `このリポジトリを観測して、EIS（OrbitLens Ace）のシグナリング設定ファイル eis.yaml を生成してください。

スキーマ: https://github.com/machuz/eis/blob/main/config.example.yaml を読み、各項目の意味を把握してから始めること。

やること（観測できた事実だけを書く。推測で埋めない）:
1. aliases: \`git log --format='%an <%ae>'\` で全 author を洗い出し、同一人物の表記揺れ（別名・複数メール）を正規名に統合する。確信が持てないものは統合しない。
2. exclude_authors: bot/自動アカウント（github-actions[bot], dependabot[bot], renovate[bot] など実在するものだけ）を除外する。
3. domains: モノレポなら repo/ディレクトリを backend/frontend/infra/firmware や独自ドメインに分類する。判別できないものは書かない（自動判定に任せる）。
4. architecture_patterns: 設計の骨格を担うファイル群をこのリポの実構造に合わせて列挙する（例: ルーター, DI, ドメインサービス, repository interface, stores, hooks）。グロブは *=1階層 / **=任意階層 / 末尾の / =そのディレクトリ配下すべて。
5. exclude_file_patterns: 生成物・ベンダ・ロックファイル等のノイズを除外する。
6. exclude_repos: 分析が不要な repo を除外する。

ルール:
- 観測した事実のみを書く。EIS の思想は「観測であり生成ではない」。
- 数値（weights, survival_tau 等）はデフォルトでよければ省略する。
- 出力はリポジトリ直下の eis.yaml 1ファイル。各設定に根拠を1行コメントで添える。

最後に、生成した eis.yaml の全文を表示してください。`;

const EN = `Observe this repository and generate eis.yaml, the EIS (OrbitLens Ace) signaling config.

Schema: read https://github.com/machuz/eis/blob/main/config.example.yaml and understand every field before you start.

Do (write only what you can observe — never fill in guesses):
1. aliases: list every author via \`git log --format='%an <%ae>'\` and merge one person's variant spellings (alternate names, multiple emails) into a canonical name. Do not merge anything you are unsure about.
2. exclude_authors: exclude bots / automation accounts (only ones that actually exist, e.g. github-actions[bot], dependabot[bot], renovate[bot]).
3. domains: for a monorepo, classify repos/directories into backend/frontend/infra/firmware or custom domains. Omit anything you cannot tell (leave it to auto-detection).
4. architecture_patterns: list the files that form the design skeleton, matched to THIS repo's real structure (e.g. routers, DI, domain services, repository interfaces, stores, hooks). Globs: * = one path segment, ** = any depth, a trailing / = everything under that directory.
5. exclude_file_patterns: exclude noise — generated files, vendored code, lockfiles.
6. exclude_repos: exclude repos that don't need analysis.

Rules:
- Write only observed facts. The EIS ethos is "observation, not generation."
- Omit numeric knobs (weights, survival_tau, etc.) if the defaults are fine.
- Output a single eis.yaml at the repo root, with a one-line comment justifying each setting.

Finally, print the full generated eis.yaml.`;

export function eisAgentPrompt(locale: Locale): string {
  return locale === "ja" ? JA : EN;
}
